/* nx-mega.js — close the Shop mega-menu when the pointer leaves it.

   The purchased theme's `products-mega-menu` element opens on hovering the
   trigger and closes on the overlay or an outside click. It has no close for
   "the pointer moved off the menu without landing on the overlay" — which is
   what happens on the way to Journal or up to the wordmark, because the header
   sits above the overlay. The menu stayed open until you clicked somewhere.

   mouseleave on the custom element, not on the trigger: the panel is a DOM
   descendant even though it is positioned out of flow, so being inside the panel
   still counts as being inside the element.

   The delay is not decoration. The panel hangs from the bottom of the header,
   not from the bottom of the trigger, so between the two there is a band of
   header that belongs to neither — and the pointer crosses it on its way down.
   Without the grace period the menu closed the instant you moved toward it,
   which is a worse bug than not closing at all. 140ms is far longer than the
   crossing takes and far shorter than a deliberate move away.

   Keyboard users are unaffected: <summary> still toggles on Enter and Space, and
   a leave event never fires for them. */
(() => {
  /* Which menus are mid-cascade. Declared here because `close` has to clear it:
     see the note on `beginCascade`. */
  const cascading = new WeakMap();

  const close = (el) => {
    const open = el.querySelector('details[open]');
    if (open) open.removeAttribute('open');
    /* ⚠ THE FLAG IS CLEARED HERE, not from the toggle event, and that is the
       whole difference between the cascade replaying and never playing again.
       `toggle` is dispatched from a queue and reads the state at DISPATCH time,
       so a close immediately followed by a reopen delivers two events that both
       report `open: true` — the close is never announced as one. Reading
       `d.open` in the handler therefore cannot be trusted to notice a close.
       This function is the one place where a close is actually intended. */
    cascading.delete(el);
  };

  /* ⚠ THE CASCADE ONLY RAN ONCE, AND ONLY ON THE FIRST OPEN.

     The columns are animated by `.nxm[open] .nxm__col`, so re-adding `[open]`
     ought to restart them. It does not: the animation has already finished, the
     element was `display: none` in between so its style was never recomputed,
     and the browser treats it as the same finished animation. Measured with
     tools/storefront/mega-stagger.mjs — first open 0.35 / 0.00 / 0.00 …, second
     open 1.00 / 1.00 / 1.00 from the very first frame. The owner found it by
     eye before the probe did, which is the whole reason the probe now takes a
     pass number.

     Clearing the animation, forcing a reflow and restoring it is what makes the
     browser treat it as new. ⚠ A FORCED REFLOW, NOT requestAnimationFrame — the
     same rule the gallery overlay already keeps: rAF can coalesce the two style
     changes into one frame, and nothing restarts. Reading `offsetWidth` cannot
     be coalesced, which is exactly why it is the tool for this. */
  const restart = (el) => {
    const targets = el.querySelectorAll('.nxm__panel, .nxm__col, .nxm__feature');
    if (!targets.length) return;
    targets.forEach((t) => { t.style.animation = 'none'; });
    void el.offsetWidth;
    targets.forEach((t) => { t.style.animation = ''; });
  };

  /* ⚠ ONE CASCADE PER OPEN, and this guard is the whole reason the menu stopped
     twitching. `toggle` fires TWICE as the pointer crosses in — measured with
     tools/storefront/mega-jitter.mjs, which moves the pointer in steps the way a
     hand does rather than teleporting it:

         toggle events: 2
            t=374ms  open=true
            t=418ms  open=true

     Both report open=true, 44ms apart: the purchased element opens, drops and
     reopens as the pointer passes over the band that belongs to neither the
     trigger nor the panel, and the queued toggle events both read the settled
     state. Without a guard the second one restarted a cascade that was already
     44ms in, throwing every column back to zero — which is exactly what the
     owner saw and called a twitch.

     Guarding the EFFECT rather than the element's open/close is deliberate.
     Fighting that element over when it may open is what made the menu strobe at
     60Hz once already; it is allowed to be indecisive, and we simply do not
     replay the animation for it. Closing rearms, so a real second visit
     cascades again. */
  const beginCascade = (el, isOpen) => {
    if (!isOpen) { cascading.delete(el); return; }
    if (cascading.has(el)) return;
    cascading.set(el, true);
    restart(el);
  };

  const bind = () => {
    document.querySelectorAll('products-mega-menu').forEach((el) => {
      if (el.dataset.nxLeaveBound === 'true') return;
      el.dataset.nxLeaveBound = 'true';

      let timer = null;
      const cancel = () => {
        if (timer) { clearTimeout(timer); timer = null; }
      };

      el.addEventListener('mouseleave', () => {
        cancel();
        timer = setTimeout(() => close(el), 140);
      });
      // Fires on re-entering the element OR any descendant, which is what makes
      // arriving in the panel cancel the pending close.
      el.addEventListener('mouseenter', cancel);

      /* The panel is opened by the native <details>, not by this file, so the
         only honest place to hear about it is the element's own toggle event.
         It fires after the attribute lands and after the panel is displayed,
         which is what the reflow above needs. */
      el.querySelectorAll('details').forEach((d) => {
        d.addEventListener('toggle', () => beginCascade(el, d.open));
      });

      /* A CLICK ON THE TRIGGER GOES TO THE SHOP — mouse only.

         The menu item already says where it goes (`Products` is /collections/all
         in Shopify) and the panel opens on hover, so on a desktop the click was
         a spare gesture that only closed what the pointer had just opened.

         ⚠ POINTER ONLY, and both halves of that matter:

         - `(hover: hover) and (pointer: fine)` keeps a touch screen out. There
           is no hover there, so the FIRST tap has to be what opens the panel; a
           tablet reaches this menu, the drawer only takes over under 750px.
         - `e.detail === 0` is a keyboard-activated click. <summary> fires a
           click for Enter and Space, and if those navigated, a keyboard user
           could never open the panel at all — the four categories would be
           unreachable. Enter and Space must keep toggling.

         Native Union does not do this, and the reason not to copy them is the
         size of the shelf: an all-products page is a wall at four hundred SKUs
         and a good page at sixteen. If the catalogue ever grows past what one
         page can hold, revisit this. */
      var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      el.querySelectorAll('[data-nx-shop-href]').forEach((sum) => {
        sum.addEventListener('click', (e) => {
          if (!fine.matches) return;
          if (e.detail === 0) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          window.location.href = sum.dataset.nxShopHref;
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }

  // The header re-renders as its own section in the theme editor, which replaces
  // the element and loses the listener.
  document.addEventListener('shopify:section:load', bind);
})();
